import { createAsyncThunk } from '@reduxjs/toolkit';
import api, { handleApiError } from 'state/api';

// Get optimization settings
export const getRepricerSettingsApi = createAsyncThunk(
  'listingRepricer/getRepricerSettings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-repricer/settings');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getRepricerSettingsApi', error, rejectWithValue);
    }
  },
);

// Update optimization settings
export const updateRepricerSettingsApi = createAsyncThunk(
  'listingRepricer/updateRepricerSettings',
  async (settings, { rejectWithValue }) => {
    try {
      const response = await api.put('listing-repricer/settings', settings);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('updateRepricerSettingsApi', error, rejectWithValue);
    }
  },
);

// Run optimization manually
export const runRepricerApi = createAsyncThunk(
  'listingRepricer/runRepricer',
  async ({ maxListings, storeId, supplierId }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (maxListings) params.append('maxListings', maxListings);
      if (storeId) params.append('storeId', storeId);
      if (supplierId) params.append('supplierId', supplierId);

      const response = await api.post(`listing-repricer/run?${params.toString()}`);
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('runRepricerApi', error, rejectWithValue);
    }
  },
);

// Reset completed listings
export const resetCompletedListingsApi = createAsyncThunk(
  'listingRepricer/resetCompletedListings',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-repricer/reset-completed');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('resetCompletedListingsApi', error, rejectWithValue);
    }
  },
);

// Get store optimizer settings
export const getStoreRepricerSettingsApi = createAsyncThunk(
  'listingRepricer/getStoreRepricerSettings',
  async (storeId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `listing-repricer/stores/${storeId}/repricer-settings`,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getStoreRepricerSettingsApi', error, rejectWithValue);
    }
  },
);

// Update store optimizer settings
export const updateStoreRepricerSettingsApi = createAsyncThunk(
  'listingRepricer/updateStoreRepricerSettings',
  async ({ storeId, updateData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `listing-repricer/stores/${storeId}/repricer-settings`,
        updateData,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('updateStoreRepricerSettingsApi', error, rejectWithValue);
    }
  },
);

// Get optimization listings
export const getRepricerListingsApi = createAsyncThunk(
  'listingRepricer/getRepricerListings',
  async ({ page = 1, limit = 50, filters = {} }, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);

      // Add all filter parameters
      if (filters.status) params.append('status', filters.status);
      if (filters.storeId) params.append('storeId', filters.storeId);
      if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
      if (filters.searchField) params.append('searchField', filters.searchField);
      if (filters.priceMin) params.append('priceMin', filters.priceMin);
      if (filters.priceMax) params.append('priceMax', filters.priceMax);
      if (filters.competitorPriceMin)
        params.append('competitorPriceMin', filters.competitorPriceMin);
      if (filters.competitorPriceMax)
        params.append('competitorPriceMax', filters.competitorPriceMax);
      if (filters.marketplace) params.append('marketplace', filters.marketplace);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.store) params.append('store', filters.store);

      const response = await api.get(
        `listing-repricer/listings?${params.toString()}`,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getRepricerListingsApi', error, rejectWithValue);
    }
  },
);

// Get listing optimization details for modal
export const getListingRepricerDetailsApi = createAsyncThunk(
  'listingRepricer/getListingRepricerDetails',
  async (listingId, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `listing-repricer/listings/${listingId}/details`,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getListingRepricerDetailsApi', error, rejectWithValue);
    }
  },
);

export const unlinkListingCompetitorApi = createAsyncThunk(
  'listingRepricer/unlinkListingCompetitor',
  async (listingId, { rejectWithValue }) => {
    try {
      const response = await api.post(
        `listing-repricer/listings/${listingId}/unlink-competitor`,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('unlinkListingCompetitorApi', error, rejectWithValue);
    }
  },
);

export const bulkUpdateListingsApi = createAsyncThunk(
  'listingRepricer/bulkUpdateListings',
  async ({ listingIds, action }, { rejectWithValue }) => {
    try {
      const response = await api.post('listing-repricer/listings/bulk-update', {
        listingIds,
        action,
      });
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('bulkUpdateListingsApi', error, rejectWithValue);
    }
  },
);

export const getRepricerProcessStatusApi = createAsyncThunk(
  'listingRepricer/getProcessStatus',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-repricer/process-status');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getRepricerProcessStatusApi', error, rejectWithValue);
    }
  },
);

// Update listing optimization
export const updateListingRepricerApi = createAsyncThunk(
  'listingRepricer/updateListingRepricer',
  async ({ listingId, updateData }, { rejectWithValue }) => {
    try {
      const response = await api.put(
        `listing-repricer/listings/${listingId}/repricer`,
        updateData,
      );
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('updateListingRepricerApi', error, rejectWithValue);
    }
  },
);

// Get optimization status counts
export const getRepricerStatusCountsApi = createAsyncThunk(
  'listingRepricer/getRepricerStatusCounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('listing-repricer/status-counts');
      return response.data.success ? response.data.data : response.data;
    } catch (error) {
      return handleApiError('getRepricerStatusCountsApi', error, rejectWithValue);
    }
  },
);
